import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, Users, HardHat, Link2, Plus, Check } from "lucide-react";
import { moneyCompact } from "@/lib/format";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const rid = () => "gc_" + Math.random().toString(36).slice(2, 10);

// Rule-based smart suggestions for a bid: crews, missing GC lines, un-indexed material.
export default function SmartSuggestionsCard({ bid, updateBid, readOnly }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const bidId = bid?.bid_id;

  const fetchIt = useCallback(() => {
    if (!bidId) return;
    api.get(`/bids/${bidId}/suggestions`).then((r) => setD(r.data)).catch(() => {});
  }, [bidId]);
  useEffect(() => { fetchIt(); }, [fetchIt]);

  if (!d) return null;
  const crews = (d.crews || []).filter((c) => c.matches && c.matches.length);
  const gc = d.gc || [];
  const idx = d.index_links || [];
  const total = crews.length + gc.length + (idx.length ? 1 : 0);

  const applyCrew = async (groupName, crew, key) => {
    setBusy(key);
    try {
      const sg = bid.scope_groups || [];
      const exists = sg.some((g) => g.name === groupName);
      const next = exists ? sg.map((g) => (g.name === groupName ? { ...g, crew } : g))
        : [...sg, { name: groupName, crew }];
      await updateBid({ scope_groups: next });
      toast.success(`Assigned crew to “${groupName}”`);
      fetchIt();
    } catch (e) { toast.error("Could not assign crew"); }
    setBusy(null);
  };

  const applyGc = async (rec) => {
    setBusy(rec.rec_key);
    try {
      const cur = bid.general_conditions || {};
      const items = [...(cur.items || []), {
        id: rid(), rec_key: rec.rec_key, label: rec.label, note: rec.note || "",
        group: rec.group, driver: rec.driver, rate: rec.rate, basis: rec.basis, enabled: true,
      }];
      await updateBid({ general_conditions: { ...cur, enabled: true, include_in_bid: cur.include_in_bid !== false, items } });
      toast.success(`Added “${rec.label}” to General Conditions`);
      fetchIt();
    } catch (e) { toast.error("Could not add GC line"); }
    setBusy(null);
  };

  return (
    <Card className="p-5 mt-4" data-testid="smart-suggestions-card">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#003E87]/[0.08] text-[#003E87]">
          <Sparkles className="w-4.5 h-4.5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Smart suggestions</h3>
          <div className="text-xs text-slate-500">
            {total === 0 ? "Nothing to flag — this estimate looks complete." : `${total} recommendation${total === 1 ? "" : "s"} to tighten this estimate`}
          </div>
        </div>
      </div>

      {total === 0 && (
        <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="smart-suggestions-empty">
          <Check className="w-3.5 h-3.5" /> Crews assigned, standard GC lines present, and material is index-linked.
        </div>
      )}

      {crews.length > 0 && (
        <div className="mt-4" data-testid="suggest-crews">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 mb-2">
            <Users className="w-3 h-3" /> Crews to assign
          </div>
          <div className="space-y-2">
            {crews.map((c) => (
              <div key={c.group_name} className="rounded-lg border border-slate-200 p-2.5" data-testid={`suggest-crew-group-${slug(c.group_name)}`}>
                <div className="text-xs font-medium text-slate-700 mb-1.5">“{c.group_name}” has time-based labor but no crew</div>
                <div className="flex flex-wrap gap-1.5">
                  {c.matches.map((m) => {
                    const key = `${slug(c.group_name)}-${m.id}`;
                    return (
                      <button key={m.id} disabled={readOnly || busy === key} onClick={() => applyCrew(c.group_name, m.crew, key)}
                        data-testid={`suggest-crew-apply-${key}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#003E87]/30 bg-[#003E87]/[0.06] text-[#003E87] hover:bg-[#003E87]/10 disabled:opacity-50 transition">
                        <Plus className="w-3 h-3" /> {m.name} <span className="text-[#003E87]/60">· {m.headcount} crew</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gc.length > 0 && (
        <div className="mt-4" data-testid="suggest-gc">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 mb-2">
            <HardHat className="w-3 h-3" /> Standard GC lines still missing
          </div>
          <div className="flex flex-wrap gap-1.5">
            {gc.map((rec) => (
              <button key={rec.rec_key} disabled={readOnly || busy === rec.rec_key} onClick={() => applyGc(rec)}
                data-testid={`suggest-gc-apply-${rec.rec_key}`}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 transition">
                <Plus className="w-3 h-3" /> {rec.label} <span className="font-mono text-amber-600">≈ {moneyCompact(rec.projected_cost)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {idx.length > 0 && (
        <div className="mt-4" data-testid="suggest-index">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 mb-2">
            <Link2 className="w-3 h-3" /> Material not index-linked ({idx.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {idx.map((l) => (
              <span key={l.line_id} data-testid={`suggest-index-${l.line_id}`}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                {l.name}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">Link a commodity index in the <b>Estimating</b> tab so these reprice automatically.</div>
        </div>
      )}
    </Card>
  );
}
